import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { PlainButton, fonts, space } from 'folio-common-components';
import { colors } from 'folio-design-tokens';
import * as React from 'react';
import { SmallArrowLeftIcon, SmallArrowRightIcon } from '../../../icons';
import { touchFeedback } from '../../../styles/touch-feedback';
import { getLedgerIcon } from '../../icon';
import type { Arrow, MainCategory, Subcategory } from './types';

type Category = MainCategory | Subcategory;

const GuessOptionIconWrapper = styled.span`
  margin-right: 16px;
  justify-self: center;
  display: flex;
  align-items: center;
  flex: none;
`;

const GuessOptionButton = styled(PlainButton)`
  padding: 16px 0;
  width: 100%;

  display: flex;
  align-items: center;

  outline: none;

  /* TODO: This is horribly slow */
  &:focus * {
    color: ${colors.blue};
  }

  @media (hover) {
    &:hover * {
      color: ${colors.blue};
    }
  }

  ${touchFeedback};
`;

const GuessOptionTextWrapper = styled.span`
  flex: 1;
`;

const guessOptionDescriptionWrapperDynamicStyles = ({
  isHighlighted,
}: {
  isHighlighted?: boolean;
}) =>
  css`
    color: ${isHighlighted ? colors.blue : colors.wcagGray};
  `;

const GuessOptionDescriptionWrapper = styled.div`
  ${guessOptionDescriptionWrapperDynamicStyles};
  color: var(--muted-color);
  margin-top: 4px;
  ${fonts.font100book};
`;

export type CategoryListOption = {
  label: string;
  description?: string | null;
  icon: React.ReactNode; // TODO: Be more specific
  arrow?: Arrow;
  isActive?: boolean;
};

type CategoryListOptionProps = CategoryListOption & {
  isHighlighted?: boolean;
  onClick?: (option: CategoryListOption, buttonEle: HTMLButtonElement) => void;
  className?: string;
};

const CategoryListOptionWithRef: React.ForwardRefRenderFunction<
  HTMLButtonElement,
  CategoryListOptionProps
> = (props, ref) => {
  const {
    label,
    description,
    icon,
    arrow,
    isActive,
    isHighlighted,
    onClick,
    className,
  } = props;
  const option: CategoryListOption = {
    label,
    description,
    icon,
    arrow,
    isActive,
  };

  return (
    <GuessOptionButton
      ref={ref}
      onClick={event => {
        onClick?.(option, event.currentTarget);
      }}
      tabIndex={isActive === false ? -1 : undefined}
      className={className}
    >
      <GuessOptionIconWrapper>
        {icon}
        {arrow === 'Left' && (
          <SmallArrowLeftIcon
            width={22} // Same with as icon
          />
        )}
      </GuessOptionIconWrapper>
      <GuessOptionTextWrapper>
        <div>{label}</div>
        {description && (
          <GuessOptionDescriptionWrapper isHighlighted={isHighlighted}>
            {description}
          </GuessOptionDescriptionWrapper>
        )}
      </GuessOptionTextWrapper>
      {arrow === 'Right' && (
        <SmallArrowRightIcon
          css={css`
            flex: none;
          `}
        />
      )}
    </GuessOptionButton>
  );
};

export const CategoryListOption = React.forwardRef(CategoryListOptionWithRef);

type CategoryListProps<T> = {
  specialOption?: {
    option: CategoryListOption;
    onSelect: () => void;
  };
  categories: readonly T[];
  showArrow?: boolean;
  onSelect: (option: T, buttonEle: HTMLButtonElement) => void;
  className?: string;
  isActive: boolean;
  backButtonRef?: React.RefObject<HTMLButtonElement>;
  'data-testid'?: string;
};

function categoryToOption(category: Category): CategoryListOption {
  return {
    label: category.title,
    description: category.subtitle,
    icon: getLedgerIcon(category.icon),
  };
}

export function CategoryList<T extends Category>({
  specialOption,
  categories,
  showArrow,
  onSelect,
  className,
  isActive,
  backButtonRef,
  ...props
}: CategoryListProps<T>) {
  return (
    <ul
      {...props}
      className={className}
      css={css`
        margin: 0;
        height: 100%;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        list-style: none;
        ${space([16], 'padding-horizontal')};
      `}
      aria-hidden={!isActive}
    >
      {specialOption && (
        <li data-testid="category-list-special-option">
          <CategoryListOption
            ref={backButtonRef}
            {...specialOption.option}
            onClick={specialOption.onSelect}
          />
        </li>
      )}

      {categories.map(category => {
        const option = categoryToOption(category);

        if (showArrow) {
          option.arrow = 'Right';
        }

        return (
          <li key={category.fid} data-testid="category-list-option">
            <CategoryListOption
              {...option}
              isActive={isActive}
              onClick={(_, buttonEle) => {
                onSelect(category, buttonEle);
              }}
            />
          </li>
        );
      })}
    </ul>
  );
}
